/**
 * AI connector sign-in (card #1310). Wire shapes and page wording for the v2 `/api/connector/*` and
 * `/api/public/connector/*` endpoints.
 * The reason codes and messages are fixed by the cross-repo contracts (card #1310 section 8.2, card #1434 section 2.4);
 * keep them word for word.
 */

export const CONNECT_AI_PATH = '/connect-ai';
/** The normal registration form for a firm that starts on the connect sign-in, with its email filled in. */
export const CONNECT_AI_SIGN_UP_PATH = '/sign-up/connect-ai';
/** The normal sign-up payment page (Zoho checkout). */
export const SIGN_UP_ORDER_PATH = '/sign-up/order';
export const EXTERNAL_AUTH_ID_PARAM = 'external_auth_id';
/** The v2 endpoint refuses a longer id with LINK_INVALID, so the page does not even send it. */
export const EXTERNAL_AUTH_ID_MAX_LENGTH = 512;
/** The portal connect page's query parameter for the email to fill in on its sign-in. */
export const LOGIN_HINT_PARAM = 'login_hint';
/** The identify endpoint refuses a longer email or username, so the input stops there. */
export const CONNECT_IDENTIFIER_MAX_LENGTH = 254;

export interface ConnectorContext {
  eligible: boolean;
  reason: string | null;
  firmName: string | null;
  personName: string | null;
  email: string | null;
}

export interface ConnectorSignInResult {
  redirectUri: string;
}

export const CONNECTOR_REASON = {
  NOT_AVAILABLE: 'NOT_AVAILABLE',
  SUPPORT_SESSION: 'SUPPORT_SESSION',
  CLIENT_ACCOUNT: 'CLIENT_ACCOUNT',
  NOT_OWNER: 'NOT_OWNER',
  DEVELOPER_ACCOUNT: 'DEVELOPER_ACCOUNT',
  ACCOUNT_DELETED: 'ACCOUNT_DELETED',
  ACCOUNT_LOCKED: 'ACCOUNT_LOCKED',
  BILLING_LOCKED: 'BILLING_LOCKED',
  SETUP_NOT_FINISHED: 'SETUP_NOT_FINISHED',
  SIGN_IN_AGAIN: 'SIGN_IN_AGAIN',
  SIGN_IN_AGAIN_TWO_STEP: 'SIGN_IN_AGAIN_TWO_STEP',
  TOO_MANY_ATTEMPTS: 'TOO_MANY_ATTEMPTS',
  LINK_INVALID: 'LINK_INVALID',
  LINK_ALREADY_USED: 'LINK_ALREADY_USED',
  FIRM_CONFLICT: 'FIRM_CONFLICT',
  ACCOUNT_CONFLICT: 'ACCOUNT_CONFLICT',
  IDENTITY_DISABLED: 'IDENTITY_DISABLED',
  FIRM_BLOCKED: 'FIRM_BLOCKED',
  TRY_AGAIN: 'TRY_AGAIN',
  CONNECT_FAILED: 'CONNECT_FAILED',
  ORIGIN_NOT_ALLOWED: 'ORIGIN_NOT_ALLOWED',
  /** The connect sign-in only: the account's Zoho billing check failed, which support has to look at. */
  ACCOUNT_CHECK: 'ACCOUNT_CHECK',
  // Card #1434: only ever answered to a login that is not the firm owner.
  SUB_ACCOUNT_LOGIN: 'SUB_ACCOUNT_LOGIN',
  ROLE_NOT_ALLOWED: 'ROLE_NOT_ALLOWED',
  TEAMMATE_LOCKED: 'TEAMMATE_LOCKED',
  AI_ACCESS_OFF: 'AI_ACCESS_OFF',
  ACCESS_HOURS_SET: 'ACCESS_HOURS_SET',
  FIRM_BILLING_LOCKED: 'FIRM_BILLING_LOCKED',
  FIRM_NOT_ACTIVE: 'FIRM_NOT_ACTIVE',
} as const;

const R = CONNECTOR_REASON;

export const CONNECTOR_MESSAGES: Readonly<Record<string, string>> = {
  [R.NOT_AVAILABLE]: 'Connecting an AI assistant is not available yet.',
  [R.SUPPORT_SESSION]: 'Support sessions cannot connect an AI assistant.',
  [R.CLIENT_ACCOUNT]: 'This sign-in is for accounting firms.',
  [R.NOT_OWNER]: "Your LedgerSync login cannot connect an AI assistant right now. Ask your firm's LedgerSync owner.",
  [R.DEVELOPER_ACCOUNT]: 'This account signs in with its email. '
    + 'Sign out, then go back to Claude or ChatGPT and click Connect again.',
  [R.ACCOUNT_DELETED]: 'This LedgerSync account is closed. Email support@ledgersync.com.',
  [R.ACCOUNT_LOCKED]: 'Your LedgerSync login is locked. Reset your password with the "Forgot password?" link '
    + 'on the sign-in page, then go back to Claude or ChatGPT and click Connect again.',
  [R.BILLING_LOCKED]: 'Your LedgerSync account is locked because a subscription payment did not go through. '
    + 'Update the card on file in the LedgerSync app, then try again.',
  [R.SETUP_NOT_FINISHED]: 'Your LedgerSync account setup is not finished. Email support@ledgersync.com.',
  [R.SIGN_IN_AGAIN]: 'Your password was changed after you signed in. Sign out, then sign in again with your new password.',
  [R.SIGN_IN_AGAIN_TWO_STEP]: 'Sign out, then sign in again with your two-step code.',
  [R.TOO_MANY_ATTEMPTS]: 'Too many tries. Wait 10 minutes, then try again.',
  [R.LINK_INVALID]: 'This connect link has expired. Go back to Claude or ChatGPT and click Connect again.',
  [R.LINK_ALREADY_USED]: 'This connect link was already used. Go back to Claude or ChatGPT and click Connect again.',
  [R.FIRM_CONFLICT]: 'This email is already connected to LedgerSync for another firm. Contact support@ledgersync.com.',
  [R.ACCOUNT_CONFLICT]: 'Another LedgerSync login already uses this email to connect an AI assistant. '
    + 'Contact support@ledgersync.com.',
  [R.IDENTITY_DISABLED]: 'Connecting an AI assistant is turned off for this account. Contact support@ledgersync.com.',
  [R.FIRM_BLOCKED]: 'Connecting an AI assistant is turned off for your firm. Contact support@ledgersync.com.',
  [R.TRY_AGAIN]: 'We could not finish connecting. Wait a minute, then click Allow again.',
  [R.CONNECT_FAILED]: 'Something went wrong on our side. Go back to Claude or ChatGPT and click Connect again. '
    + 'If it keeps happening, email support@ledgersync.com.',
  [R.ACCOUNT_CHECK]: 'Your LedgerSync account needs a quick check. Email support@ledgersync.com.',
  [R.SUB_ACCOUNT_LOGIN]: 'Connecting an AI assistant is not available for sub-account logins yet.',
  [R.ROLE_NOT_ALLOWED]: 'This LedgerSync login cannot connect an AI assistant.',
  [R.TEAMMATE_LOCKED]: "Your LedgerSync login is locked. Ask your firm's LedgerSync owner to unlock it.",
  [R.AI_ACCESS_OFF]: "Your firm's LedgerSync owner turned off AI assistants for your login. "
    + 'Ask them to turn it back on.',
  [R.ACCESS_HOURS_SET]: 'Your LedgerSync login has access days and hours, and AI assistants do not support them yet. '
    + 'Email support@ledgersync.com.',
  [R.FIRM_BILLING_LOCKED]: "Your firm's LedgerSync account is locked because a subscription payment did not go through. "
    + "Ask your firm's LedgerSync owner.",
  [R.FIRM_NOT_ACTIVE]: "LedgerSync is not active for your firm right now. Ask your firm's LedgerSync owner.",
};

/** Shown by the reset-password pages when the reset started from the connect version of the login page. */
export const CONNECT_AI_RESET_REQUESTED_MESSAGE = 'We sent you an email to reset your password. '
  + 'After you set a new password, go back to Claude or ChatGPT and click Connect again. '
  + 'Any AI assistant you connected before is disconnected.';
export const CONNECT_AI_RESET_DONE_MESSAGE = 'Your password is reset. Go back to Claude or ChatGPT and click Connect again. '
  + 'Any AI assistant you connected before is disconnected.';

/** What `POST /api/public/connector/identify` says the connect sign-in does next with the typed email or username. */
export const CONNECT_IDENTIFY_NEXT = {
  /** A LedgerSync username, or the email of exactly one LedgerSync login: ask for the password. */
  PASSWORD: 'PASSWORD',
  /** An email whose LedgerSync logins are developer accounts: continue on the developer portal sign-in with it. */
  EMAIL: 'EMAIL',
  /** An email that more than one LedgerSync login uses: ask for the username. */
  USERNAME: 'USERNAME',
  /** Not an email, and no LedgerSync login has that username. */
  NOT_FOUND: 'NOT_FOUND',
  /** An email no LedgerSync login uses: offer to create a LedgerSync account with it. */
  CREATE: 'CREATE',
  /** An email that is the username of a closed LedgerSync account: registration would refuse it. */
  CLOSED: 'CLOSED',
  /** An email registration refuses on its domain (free mail): ask for a work email. */
  WORK_EMAIL: 'WORK_EMAIL',
} as const;

export type ConnectIdentifyNext = typeof CONNECT_IDENTIFY_NEXT[keyof typeof CONNECT_IDENTIFY_NEXT];

export interface ConnectIdentifyResult {
  next: ConnectIdentifyNext;
}

/** The locked login message on the password screen. Its middle words link to the password reset. */
export const CONNECT_LOCKED_MESSAGE_PARTS = {
  BEFORE: 'Too many wrong passwords.',
  LINK: 'Reset your password',
  AFTER: 'to continue.',
} as const;

/** The connect sign-in's own short messages, shown under the input of the screen they belong to. */
export const CONNECT_SIGN_IN_MESSAGES = {
  NOT_FOUND: "We can't find that username. Try your email.",
  USERNAME: 'More than one account uses this email. Enter your username.',
  TOO_MANY_ATTEMPTS: 'Too many tries. Wait 10 minutes and try again.',
  WRONG_PASSWORD: 'Wrong password. Try again.',
  LOCKED: `${CONNECT_LOCKED_MESSAGE_PARTS.BEFORE} ${CONNECT_LOCKED_MESSAGE_PARTS.LINK} ${CONNECT_LOCKED_MESSAGE_PARTS.AFTER}`,
  FAILED: 'Something went wrong. Try again.',
  CLOSED: 'This email belongs to a closed LedgerSync account. Email support@ledgersync.com.',
  WORK_EMAIL: 'Use your work email to create a LedgerSync account.',
  /** An email no LedgerSync login uses, which the sign-up form refuses because of its plus sign. */
  PLUS_EMAIL: 'Use an email without a plus sign (+) to create a LedgerSync account.',
  /** An email no LedgerSync login uses, which the sign-up form refuses for anything else (an apostrophe, no dot). */
  EMAIL_FORMAT: 'Check your email and try again.',
  /** The password screen after a payment this browser just made, while the account is still being set up. */
  PAID_WAIT: 'Getting your account ready. Wait 1 minute, then click Sign in.',
} as const;

/** The connect sign-in step for an email no LedgerSync login uses. */
export const CONNECT_CREATE_ACCOUNT_COPY = {
  TITLE: 'Create your LedgerSync account',
  TEXT: 'No LedgerSync account uses this email yet.',
  BUTTON: 'Create account',
  HELP: 'Already use LedgerSync? Click Change and type your username.',
} as const;

/** The step, on the connect sign-in and on the connect page, for an account whose sign-up was never paid. */
export const CONNECT_FINISH_ACCOUNT_COPY = {
  TITLE: 'Finish creating your account',
  TEXT: 'This LedgerSync account is not finished yet.',
  BUTTON: 'Finish',
} as const;

/** The title of the connect page. */
export const CONNECT_AI_PAGE_TITLE = 'Connect your AI assistant';

/** The connect page right after a payment, while the account is being set up. Never offers payment again. */
export const CONNECT_AI_WAIT_COPY = {
  TEXT: 'Getting your account ready. One moment...',
  TIMED_OUT: 'Your account is almost ready. Wait 2 minutes, then click Try again.',
  BUTTON: 'Try again',
} as const;

/** How often that wait asks again whether the account is ready, and for how long before it shows Try again. */
export const CONNECT_AI_WAIT_POLL_MS = 3 * 1000;
export const CONNECT_AI_WAIT_MAX_MS = 90 * 1000;

/** Allow found the connect link expired or used after the person created their account on this browser. */
export const CONNECT_AI_ACCOUNT_READY_MESSAGE = 'Your LedgerSync account is ready. '
  + 'Go back to Claude or ChatGPT and click Connect again.';

/** The sign-up completion page title when the sign-up started from the connect sign-in. */
export const CONNECT_AI_COMPLETING_TITLE = 'Creating your account. One moment...';
